export { EventStatus } from './EventStatus';

export interface VisitorPassUserProfileType {
  email: string;
  phone: string;
  image: string;
}

export interface VisitorPassHostType {
  _id: string;
  name: string;
  profile: VisitorPassUserProfileType;
}

/** Canonical minimal visitor shape for visitor pass and watchlist types. Re-exported by application-types. */
export interface ActivateVisitor {
  id: string;
  channelId?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  company?: string;
  createdAtDatetime?: string;
  updatedAtDatetime?: string;
  createdByUserId: string;
  updatedByUserId?: string;
}

/** Minimal recurrence shape for query responses (mirrors lane-shared RecurrenceQueryResponse). */
export interface RecurrenceQueryResponse {
  id: string;
  visitorIds: string[];
  schedule: string;
  scheduleDuration: string;
  scheduleType: string;
  status: string;
  sourceId?: string;
  submissionId?: string;
  hostUserId: string;
  hostTenantId: string;
  accessGrantBuildingId: string;
  visitorNote?: string;
  staffNote?: string;
  floor?: string;
  createdByUserId: string;
  createdAtDatetime?: string;
  updatedAtDatetime?: string;
  projectedAtDatetime?: string;
  lastUpdatedByUserId?: string;
}

export interface VisitorPassQueryResponse {
  id: string;
  visitor: ActivateVisitor;
  sourceId?: string; // Content Id
  submissionId?: string; // UCI Id
  submissionVisitorId?: string;
  recurrenceId?: string;
  createdByUserId: string;
  hostUserId: string;
  hostTenantId: string;
  hostName: string;
  tenantName: string;
  host?: VisitorPassHostType;
  tenant?: Record<string, unknown>; // Partial<Channel> from lane-shared
  floor?: string;
  accessGrantBuildingId: string; // Channel / Building Id
  companyName: string;
  status: string;
  visitorNote?: string;
  staffNote?: string;
  eventName: string;
  startDatetime?: string;
  endDatetime?: string;
  createdAtDatetime?: string;
  updatedAtDatetime?: string;
  submittedBy: string;
  updatedBy: string;
  validStateTransitions: string[];
  checkedInDatetime?: string;
  checkedOutDatetime?: string;
  currentVersion: number;
  whiteLabelId: string;
  additionalRecipients?: string[];
  visitorWatchlistMatchInfo?: VisitorWatchlistMatchInfo[];
  watchlistDetails?: VisitorWatchlistDetails;
  inviteName?: string;
}

export type VisitorPassesAndRecurrencesQueryResponse = {
  visitorPasses: VisitorPassQueryResponse[];
  recurrences: RecurrenceQueryResponse[];
};

export type VisitorPassChangeDetails = {
  visitorPassId: string;
  changeNote: string;
  startDatetime: Date | undefined;
  endDatetime: Date | undefined;
};

export enum WatchlistSeverityLevel {
  WATCHLIST_SEVERITY_LEVEL_INVALID = 'WATCHLIST_SEVERITY_LEVEL_INVALID',
  WATCHLIST_SEVERITY_LEVEL_HIGH = 'WATCHLIST_SEVERITY_LEVEL_HIGH',
  WATCHLIST_SEVERITY_LEVEL_MEDIUM = 'WATCHLIST_SEVERITY_LEVEL_MEDIUM',
  WATCHLIST_SEVERITY_LEVEL_LOW = 'WATCHLIST_SEVERITY_LEVEL_LOW',
  UNRECOGNIZED = 'UNRECOGNIZED',
}

export interface VisitorWatchlistMatchInfo {
  watchlistSeverityLevel: WatchlistSeverityLevel;
  watchlistId?: string;
  isApprovalRequired?: boolean;
  isWatchlistDisplayed?: boolean;
  displayText?: string;
}

export interface VisitorMatchingRule {
  primaryValue: string;
  alternateValues: string[];
  matchType: string;
}

export interface VisitorMatchingInfo {
  firstName?: VisitorMatchingRule;
  lastName?: VisitorMatchingRule;
  email?: VisitorMatchingRule;
  phone?: VisitorMatchingRule;
}

export interface VisitorWatchlist {
  id: string;
  organizationId: string;
  visitorMatchingInfo?: VisitorMatchingInfo;
  severityLevel: string;
  notes?: string;
  attachmentIds?: string[];
  reportedBy?: string;
  createdBy?: string;
  createdByName?: string;
  updatedBy?: string;
  updatedByName?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface VisitorWatchlistMatchDetails {
  visitorWatchlistId: string;
  severityLevel: string;
  displayLevelText: string;
  matchedFields: string[];
  notes?: string;
  visitorWatchlist?: VisitorWatchlist;
}

export interface VisitorWatchlistDetails {
  matches: VisitorWatchlistMatchDetails[];
  pendingSubmissionVisitors?: Array<{
    id: string;
    visitor: ActivateVisitor;
    status: string;
    startDatetime?: string;
    endDatetime?: string;
  }>;
}
